import { useCallback, useState } from 'react';
import { SelectedComicOptionEnum, StorageKeyEnum } from '@constant';
import { useFetchComicNameAutocomplete, useFetchComicNameList } from '@api';
import { IComicNameListItem } from '@types';
import { debounce } from 'lodash';
import { useComicActions } from '@remote-hooks';
import { useSelector } from '@stores';

//Output 페이지 장르 및 작품명 관련 로직에 관여
export const useComic = () => {
  const [autocompleteKeyword, setAutocompleteKeyword] = useState<string>('');
  const [inputKeyword, setInputKeyword] = useState<string>('');
  const [isTagging, setIsTagging] = useState(false); // 엔터 입력 감지 상태

  const selectedOption = useSelector((state) => state.comic.selectedOption);
  const selectedGenreList = useSelector((state) => state.comic.genreList);
  const selectedComicNameList = useSelector((state) => state.comic.comicNameList);

  const { onSetComic } = useComicActions();

  const COMIC_LIST_EXPIRATION_INTERVAL = 86400000; // 1일

  const { data: comicNameAutocompleteData } = useFetchComicNameAutocomplete(
    autocompleteKeyword && autocompleteKeyword.length > 0 ? autocompleteKeyword : null
  );
  const autocompleteList =
    autocompleteKeyword &&
    autocompleteKeyword.length > 0 &&
    comicNameAutocompleteData &&
    comicNameAutocompleteData.length > 0
      ? comicNameAutocompleteData.map((item) => item.name)
      : [];

  const { refetch: refetchComicNameList } = useFetchComicNameList();

  // 스탠다드 검색 Output '작품 정보로 검색' 토글 OFF할 시 장르 및 작품명 초기화
  const initComic = () => {
    onSetComic({ selectedOption: SelectedComicOptionEnum.GENRE, genreList: [], comicNameList: [] });
  };

  // 라디오 버튼 선택 핸들러
  const handleOptionChange = (option: SelectedComicOptionEnum) => {
    if (option === SelectedComicOptionEnum.GENRE) {
      // 장르 선택 시 초기화
      onSetComic({ selectedOption: SelectedComicOptionEnum.GENRE, genreList: [], comicNameList: [] });
      setInputKeyword('');
      setAutocompleteKeyword('');
    } else {
      // 작품명 선택 시 초기화
      onSetComic({ selectedOption: SelectedComicOptionEnum.COMIC_NAME, genreList: [], comicNameList: [] });
    }
  };

  const handleGenreToggle = (genre: string) => {
    const isCurrentlySelected = selectedGenreList.includes(genre);

    if (isCurrentlySelected) {
      // 이미 선택된 장르라면 제거
      onSetComic({ genreList: selectedGenreList.filter((g) => g !== genre) });
    } else {
      // 선택되지 않은 장르라면 추가
      onSetComic({ genreList: [...selectedGenreList, genre] });
    }
  };

  // 특정 장르의 선택 여부 확인
  const isGenreSelected = (genre: string) => {
    return selectedGenreList.includes(genre);
  };

  // 브라우저에 캐시된 작품명 리스트 데이터 가져오기
  const loadCachedComicNameList = (): { comicNameList: IComicNameListItem[] | null; expiredDate: number | null } => {
    const cachedData = localStorage.getItem(StorageKeyEnum.COMIC_NAME_LIST_DATA_KEY);
    if (!cachedData) return { comicNameList: null, expiredDate: null };

    try {
      const parsedData = JSON.parse(cachedData);
      return {
        comicNameList: parsedData.comicNameList || null,
        expiredDate: parsedData.expiredDate || null
      };
    } catch (error) {
      console.error('Failed to parse cached comic list:', error);
      return { comicNameList: null, expiredDate: null };
    }
  };

  // GET /comics 요청 및 데이터 저장
  const fetchComicNameList = async () => {
    try {
      const response = await refetchComicNameList();
      const fetchedComicNameList =
        response?.data?.data.data && response?.data?.data.data.length > 0 ? response.data.data.data : []; // API 응답에서 작품명 리스트 추출
      const now = Date.now();

      // 로컬 스토리지에 작품명 리스트와 만료 시간 저장
      localStorage.setItem(
        StorageKeyEnum.COMIC_NAME_LIST_DATA_KEY,
        JSON.stringify({
          comicNameList: fetchedComicNameList,
          expiredDate: now + COMIC_LIST_EXPIRATION_INTERVAL
        })
      );

      return fetchedComicNameList;
    } catch (error) {
      console.error('Error fetching comic list:', error);
      return null;
    }
  };

  const handleComicTagsUpdate = (tags: string[]) => {
    onSetComic({ comicNameList: [...tags] });
  };

  // 엔터 입력 시 태그 추가, handleInputKeywordChange보다 먼저 실행됨
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'Enter': {
        e.preventDefault(); // 스페이스 입력 방지
        if (inputKeyword.trim().length > 0) {
          addComicTag(inputKeyword);
          setIsTagging(true); // 태그 추가 플래그 설정
          setAutocompleteKeyword('');
          setInputKeyword(''); // 즉시 입력 필드 비우기
        }
        break;
      }
      case 'Backspace': {
        if (inputKeyword === '' && selectedComicNameList && selectedComicNameList.length > 0) {
          handleComicTagsUpdate(selectedComicNameList.slice(0, -1)); // 마지막 태그 삭제
        }
        break;
      }

      default: {
        return;
      }
    }
  };

  const handleInputKeywordChange = (event: React.SyntheticEvent, newValue: string) => {
    if (isTagging) {
      setIsTagging(false); // 태그 추가 후 `onInputChange` 무시
    }

    // `nativeEvent`를 통해 실제 키보드 이벤트 가져오기
    const keyboardEvent = event.nativeEvent as KeyboardEvent;

    // 엔터 입력 시: 태그 추가 & 입력 필드 초기화
    if (keyboardEvent.key === 'enter') {
      event.preventDefault(); // 기본 동작 방지
      if (newValue.trim().length > 0) {
        addComicTag(newValue);
        setInputKeyword(''); // 입력 필드 초기화
      }
      return;
    }

    // 백스페이스 입력 시: 마지막 태그 삭제
    if (keyboardEvent.key === 'Backspace' && newValue === '' && selectedComicNameList.length > 0) {
      handleComicTagsUpdate(selectedComicNameList.slice(0, -1));
      return;
    }

    // 일반 문자 입력 시: 상태 업데이트
    if (newValue.trim().length > 0) {
      setInputKeyword(newValue);
      handleAutocompleteKeywordChange(newValue);
    } else {
      setInputKeyword('');
      handleAutocompleteKeywordChange(newValue);
    }
  };

  // 오토컴플리트 리스트 불러오기에 이용할 키워드 setState
  const handleAutocompleteKeywordChange = useCallback(
    debounce((newValue: string) => {
      setAutocompleteKeyword(newValue);
    }, 500),
    []
  );

  // 작품명 태그 추가 함수
  const addComicTag = async (tag: string) => {
    const trimmedTag = tag.trim();

    if (
      !trimmedTag ||
      (selectedComicNameList && selectedComicNameList.length > 0 && selectedComicNameList.includes(trimmedTag))
    ) {
      return;
    }

    // 캐시된 작품명 전체 리스트를 로드
    const { comicNameList: cachedComicNameList, expiredDate } = loadCachedComicNameList();
    const now = Date.now();

    let validComicNameList = cachedComicNameList;

    // 캐시가 없거나 만료되었으면 새로 요청
    if (!cachedComicNameList || !expiredDate || now > expiredDate) {
      validComicNameList = await fetchComicNameList();
    }

    // 오토컴플리트 리스트에 trimmedTag가 존재할 시, DB comic 테이블에 존재하는 값이므로 force add 한다
    let needForceAdd = false;
    if (autocompleteList.includes(trimmedTag)) {
      needForceAdd = true;
    }

    // 태그가 유효한지(DB comic 테이블에 존재하는 값인지) 검사 후 추가
    if (needForceAdd || (validComicNameList && validComicNameList.some((item) => item.name === trimmedTag))) {
      setAutocompleteKeyword('');
      // debounce된 함수 호출 취소하여 setAutocompleteKeyword(value) 재실행 방지
      handleAutocompleteKeywordChange.cancel();
      handleComicTagsUpdate([...selectedComicNameList, trimmedTag]);
    } else {
      console.log('Inexist comic');
    }
  };

  // 오토컴플리트에서 작품명 선택 시 추가 (검색어는 초기화)
  const handleAutocompleteChange = (event: React.SyntheticEvent, newValue: string | null) => {
    if (newValue && typeof newValue === 'string' && newValue.trim().length > 0) {
      addComicTag(newValue);
      setAutocompleteKeyword('');
      setInputKeyword('');

      // debounce된 함수 호출 취소하여 setAutocompleteKeyword(value) 재실행 방지
      handleAutocompleteKeywordChange.cancel();
    }
  };

  // 단일 작품명 태그 삭제
  const handleComicTagClear = (tagToRemove: string) => {
    handleComicTagsUpdate(selectedComicNameList.filter((tag) => tag !== tagToRemove));
  };

  // 작품명 인풋 텍스트 삭제
  const initComicNameInput = () => {
    setAutocompleteKeyword('');
    setInputKeyword('');
  };

  return {
    //장르&작품명 초기화
    initComic,

    //장르&작품명 라디오 버튼
    selectedOption,
    handleOptionChange,

    //장르
    selectedGenreList,
    handleGenreToggle,
    isGenreSelected,

    //작품명
    selectedComicNameList,
    inputKeyword,
    autocompleteList,
    handleKeyDown,
    handleInputKeywordChange,
    handleAutocompleteKeywordChange,
    handleAutocompleteChange,
    handleComicTagClear,
    initComicNameInput
  };
};
